import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SpotlightCompany } from '@/api/v4/spotlight.api';
import { useListingStages } from '@/features/listing-stage/use-listing-stages.query';
import { useCurrentUser } from '@/user/use-current-user.query';

import type { StagePillKey } from '../stage-pills/spotlight-company-stage-pills';
import { useSpotlightSearchTracking } from '../tracking/use-spotlight-search-tracking';
import {
  useSpotlightCompanies,
  useSpotlightCompaniesKpi,
  type SpotlightCompanySortMode,
} from './use-spotlight-companies.query';
import {
  DEFAULT_STAGE_FILTERS,
  getSelectedListingStageIds,
  useSpotlightDefaultFilters,
} from './use-spotlight-default-filters';

export const PAGE_SIZE = 12;
export const CARD_ANIMATION_STAGGER_MS = 40;

type SpotlightFilterMode = 'editorsOnly' | 'stagesOnly' | 'stagesOrEditors';

export const useSpotlightCompaniesListing = () => {
  const { data: user } = useCurrentUser();
  const {
    filters: defaultSpotlightFilters,
    isReady: isDefaultSpotlightFiltersReady,
  } = useSpotlightDefaultFilters();
  const { isLoading: isListingStagesLoading, listingStages } =
    useListingStages();
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [isStageFilterOpen, setIsStageFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [sortMode, setSortMode] =
    useState<SpotlightCompanySortMode>('lastAdded');
  const [selectedStageFilters, setSelectedStageFilters] = useState<
    StagePillKey[]
  >([]);
  const [searchValue, setSearchValue] = useState('');
  const [renderedCompanies, setRenderedCompanies] = useState<
    SpotlightCompany[]
  >([]);
  const [renderedTotalItems, setRenderedTotalItems] = useState(0);
  const effectiveSelectedStageFilters =
    selectedStageFilters.length > 0
      ? selectedStageFilters
      : DEFAULT_STAGE_FILTERS;
  const effectiveListingStageFilters = useMemo(
    () =>
      effectiveSelectedStageFilters.filter(
        stageFilter => stageFilter !== 'editorsChoice',
      ),
    [effectiveSelectedStageFilters],
  );
  const isUsingDefaultStageSelection = selectedStageFilters.length === 0;
  const selectedListingStageIds = useMemo(() => {
    if (isUsingDefaultStageSelection) {
      return defaultSpotlightFilters.listingStages;
    }

    return getSelectedListingStageIds(
      effectiveListingStageFilters,
      listingStages,
    );
  }, [
    defaultSpotlightFilters.listingStages,
    effectiveListingStageFilters,
    isUsingDefaultStageSelection,
    listingStages,
  ]);
  const hasEditorsChoiceFilter =
    effectiveSelectedStageFilters.includes('editorsChoice');
  const hasListingStageFilters = effectiveListingStageFilters.length > 0;
  const activeFilterMode: SpotlightFilterMode = hasEditorsChoiceFilter
    ? hasListingStageFilters
      ? 'stagesOrEditors'
      : 'editorsOnly'
    : 'stagesOnly';
  let isFilterMappingReady = true;
  if (isUsingDefaultStageSelection) {
    isFilterMappingReady = isDefaultSpotlightFiltersReady;
  } else if (activeFilterMode !== 'editorsOnly') {
    isFilterMappingReady =
      hasListingStageFilters &&
      !isListingStagesLoading &&
      selectedListingStageIds.length > 0;
  }
  const filters = useMemo(() => {
    if (isUsingDefaultStageSelection) {
      return defaultSpotlightFilters;
    }

    if (activeFilterMode === 'editorsOnly') {
      return { isEditorChoice: true };
    }

    if (activeFilterMode === 'stagesOnly') {
      return { listingStages: selectedListingStageIds };
    }

    return {
      isEditorChoice: true,
      listingStages: selectedListingStageIds,
    };
  }, [
    activeFilterMode,
    defaultSpotlightFilters,
    isUsingDefaultStageSelection,
    selectedListingStageIds,
  ]);

  const {
    data: companies,
    isFetching,
    isLoading,
  } = useSpotlightCompanies(
    page,
    PAGE_SIZE,
    searchValue,
    sortMode,
    filters,
    isFilterMappingReady,
  );
  const {
    data: kpi,
    isFetching: isKpiFetching,
    isLoading: isKpiLoading,
  } = useSpotlightCompaniesKpi(searchValue, filters, isFilterMappingReady);

  useSpotlightSearchTracking({
    isFetching: isFetching || isKpiFetching,
    page,
    resultsCount: typeof kpi?.companies === 'number' ? kpi.companies : null,
    searchValue,
    userId: user?.id ?? null,
  });

  useEffect(() => {
    setPage(1);
  }, [searchValue, sortMode, selectedStageFilters]);

  useEffect(() => {
    if (companies) {
      setRenderedCompanies(companies);
      return;
    }

    if (!isFetching) {
      setRenderedCompanies([]);
    }
  }, [companies, isFetching]);

  useEffect(() => {
    if (typeof kpi?.companies === 'number') {
      setRenderedTotalItems(kpi.companies);
      return;
    }

    if (!isKpiLoading) {
      setRenderedTotalItems(0);
    }
  }, [isKpiLoading, kpi?.companies]);

  const shouldShowLoadingSkeletons =
    isLoading && renderedCompanies.length === 0;

  const handleSearchPhraseChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleRemoveSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  const handleToggleStageFilter = useCallback((stageFilter: StagePillKey) => {
    setSelectedStageFilters(previous =>
      previous.includes(stageFilter)
        ? previous.filter(stage => stage !== stageFilter)
        : [...previous, stageFilter],
    );
  }, []);

  const handleToggleStageFiltersPanel = useCallback(() => {
    setIsStageFilterOpen(previous => !previous);
  }, []);

  const handleClearStageFilters = useCallback(() => {
    setSelectedStageFilters([]);
  }, []);

  const handleCloseStageFiltersPanel = useCallback(() => {
    setIsStageFilterOpen(false);
  }, []);

  const hasActiveStageFilters = selectedStageFilters.length > 0;
  const isSortControlActive = isSortDropdownOpen;
  const isFilterControlActive = isStageFilterOpen || hasActiveStageFilters;

  return {
    displayedCompanies: renderedCompanies,
    displayedTotalItems: renderedTotalItems,
    handleClearStageFilters,
    handleCloseStageFiltersPanel,
    handleRemoveSearch,
    handleSearchPhraseChange,
    handleToggleStageFilter,
    handleToggleStageFiltersPanel,
    hasActiveStageFilters,
    isFilterControlActive,
    isSortControlActive,
    isStageFilterOpen,
    listingStages,
    page,
    searchValue,
    selectedStageFilters,
    setIsSortDropdownOpen,
    setPage,
    setSortMode,
    shouldShowLoadingSkeletons,
    sortMode,
  };
};
